import React from 'react';
import { cn } from "../../../lib/utils";

/**
 * Section Component (Section.xsx)
 *
 * Purpose: Provides a reusable structural component for displaying content
 * within a bordered section with an optional header (icon, title, actions).
 *
 * Props:
 * - title (string | React.ReactNode, optional): Content for the section heading.
 * - icon (React.ComponentType, optional): Icon component (e.g., from lucide-react) to display before the title. Props
 *   like className will be passed.
 * - headerActions (React.ReactNode, optional): JSX (e.g., buttons) to display on the right side of the header.
 * - children (React.ReactNode, required): The content for the main body of the section.
 * - className (string, optional): Additional classes for the outer container div.
 * - headerClassName (string, optional): Additional classes for the header div.
 * - bodyClassName (string, optional): Additional classes for the body div.
 *
 * Adherence to Guidelines:
 * - 8pt Grid: Padding (p-4=16px, p-6=24px), margins (mt-4=16px), gaps (gap-2=8px, gap-4=16px) adhere to the 4/8pt grid.
 * - Typography: Header uses Semibold weight (Size 2/3 suggested). Body content uses Regular weight (Size 3 suggested).
 *   Icon size controlled externally or via defaults.
 * - Color: Uses semantic colors like bg-background, border-border, text-foreground, text-muted-foreground.
 * - Structure: Clean, logical grouping of header and body content.
 */

interface SectionProps {
    title?: string | React.ReactNode;
    icon?: React.ComponentType<{ className?: string }>;
    headerActions?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    headerClassName?: string;
    bodyClassName?: string;
  }

const Section: React.FC<SectionProps> = ({
  title,
  icon: IconComponent,
  headerActions,
  children,
  className,
  headerClassName,
  bodyClassName
}) => {

  // Determine if the header has any content to render
  const hasHeaderContent = title || IconComponent || headerActions;

  return (
    // Outer container: Provides margin, border, background, rounded corners
    // better shadows, and hover effect
    <div className={cn("mt-1", className)}>

      {/* Header Section: Only renders if there's content for it. */}
      {hasHeaderContent && (
        // Adheres to 8pt Grid (p-4=16px, gap-4=16px)
        <div className={cn("flex items-center pb-2", headerClassName)}>
          {/* Render Icon if provided */}
          {IconComponent && (
            // Adheres to 8pt Grid (mr-2 = 8px)
            <IconComponent
              className="h-5 w-5 mr-3 text-primary/80 flex-shrink-0"
              aria-hidden="true"
            />
          )}

          {/* Render Title if provided */}
          {/* Adheres to Typography (Size 2/3 suggested, Semibold weight) */}
          {title && <h3 className="text-base font-medium text-foreground tracking-tight">{title}</h3>}

          {/* Render Header Actions if provided, pushed to the right */}
          {/* Adheres to 8pt Grid (ml-auto, pl-4=16px, gap-2=8px) */}
          {headerActions && <div className="ml-auto flex items-center gap-3 pl-4">{headerActions}</div>}
        </div>
      )}

      {/* Body Section: Renders the content passed as children. */}
      {/* Adheres to 8pt Grid (p-4=16px or p-6=24px could be options) */}
      {/* Adheres to Typography (Size 3 suggested, Regular weight for typical body text) */}
      {/* Changelog - Body Section: Improved padding and spacing */}
      <div className={cn("py-5 text-foreground/90", bodyClassName)}>
        {children}
      </div>

    </div>
  );
};

export { Section };
